import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Command, ArrowRight, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function ResetPassword() {
    const [searchParams] = useSearchParams();
    const token = searchParams.get("token");
    const [password, setPassword] = useState("");
    const [confirm, setConfirm] = useState("");
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);

    const onSubmit = async (e) => {
        e.preventDefault();
        if (password !== confirm) {
            toast.error("Passwords do not match");
            return;
        }
        if (password.length < 8) {
            toast.error("Password must be at least 8 characters");
            return;
        }
        setLoading(true);
        try {
            await api.post("/auth/reset-password", { token, new_password: password });
            setSuccess(true);
            toast.success("Password reset successfully");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to reset password");
        } finally {
            setLoading(false);
        }
    };

    if (!token) {
        return (
            <div className="min-h-screen flex items-center justify-center p-8" data-testid="reset-password-page">
                <div className="w-full max-w-sm text-center">
                    <h1 className="text-2xl font-display font-bold tracking-tight mb-4">Invalid Link</h1>
                    <p className="text-sm text-muted-foreground mb-6">
                        This password reset link is invalid or has expired.
                    </p>
                    <Link to="/login" className="text-gold font-medium hover:underline">
                        Back to login
                    </Link>
                </div>
            </div>
        );
    }

    if (success) {
        return (
            <div className="min-h-screen flex items-center justify-center p-8" data-testid="reset-password-page">
                <div className="w-full max-w-sm text-center animate-fade-up">
                    <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-4" />
                    <h1 className="text-2xl font-display font-bold tracking-tight mb-2">Password Reset</h1>
                    <p className="text-sm text-muted-foreground mb-6">
                        Your password has been reset successfully.
                    </p>
                    <Link to="/login">
                        <Button className="bg-foreground text-background hover:bg-neutral-800 hover:text-white">
                            Sign in <ArrowRight className="w-4 h-4 ml-1.5" />
                        </Button>
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen flex items-center justify-center p-8" data-testid="reset-password-page">
            <div className="w-full max-w-sm animate-fade-up">
                <Link to="/" className="flex items-center gap-2 mb-10" data-testid="brand-reset">
                    <div className="w-9 h-9 bg-foreground text-background flex items-center justify-center rounded-md">
                        <Command className="w-4 h-4" />
                    </div>
                    <div>
                        <div className="font-display font-bold tracking-tight">Closvio</div>
                        <div className="text-[10px] uppercase tracking-wider text-muted-foreground">M&A Platform</div>
                    </div>
                </Link>

                <h1 className="text-3xl font-display font-bold tracking-tight mb-1">Reset password</h1>
                <p className="text-sm text-muted-foreground mb-8">Enter your new password below.</p>

                <form onSubmit={onSubmit} className="space-y-4" data-testid="reset-password-form">
                    <div className="space-y-2">
                        <Label htmlFor="password" className="text-xs uppercase tracking-wider text-muted-foreground">New password</Label>
                        <Input
                            id="password"
                            type="password"
                            required
                            minLength={8}
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            data-testid="reset-password-input"
                            placeholder="Min. 8 characters"
                        />
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="confirm" className="text-xs uppercase tracking-wider text-muted-foreground">Confirm password</Label>
                        <Input
                            id="confirm"
                            type="password"
                            required
                            minLength={8}
                            value={confirm}
                            onChange={(e) => setConfirm(e.target.value)}
                            data-testid="reset-confirm-input"
                            placeholder="Re-enter password"
                        />
                    </div>
                    <Button
                        type="submit"
                        disabled={loading}
                        className="w-full bg-foreground text-background hover:bg-neutral-800 hover:text-white"
                        data-testid="reset-submit-btn"
                    >
                        {loading ? "Resetting…" : "Reset password"}
                    </Button>
                </form>

                <p className="text-sm text-muted-foreground mt-6">
                    <Link to="/login" className="text-foreground font-medium underline underline-offset-4 hover:no-underline">
                        Back to login
                    </Link>
                </p>
            </div>
        </div>
    );
}
